// vgl https://firebase.googleblog.com/2018/03/cleanse-your-angular-components.html

import { Component, OnInit, Inject } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AuthService } from '../services/auth.service';
import { Observable } from 'rxjs';
import firebase from 'firebase/compat/app';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: "app-login",
  template: `
    <div class="min-h-screen bg-gray-50">
      <!-- Mobile Layout (Login Only) -->
      <div class="block md:hidden w-full max-w-md mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div>
          <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {{ isLoginMode ? 'Log in to your account' : 'Create a new account' }}
          </h2>
        </div>
        <form class="mt-8 space-y-6" [formGroup]="authForm" (ngSubmit)="onSubmit()">
          <div class="rounded-md shadow-sm -space-y-px">
            <div>
              <label for="email-address-mobile" class="sr-only">Email address</label>
              <input id="email-address-mobile" name="email" type="email" formControlName="email" required class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm" placeholder="Email address">
            </div>
            <div>
              <label for="password-mobile" class="sr-only">Password</label>
              <input id="password-mobile" name="password" type="password" formControlName="password" required class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm" placeholder="Password">
            </div>
          </div>

          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <input id="remember-me-mobile" formControlName="rememberMe" type="checkbox" class="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded">
              <label for="remember-me-mobile" class="ml-2 block text-sm text-gray-900">
                Remember me
              </label>
            </div>

            <div class="text-sm">
              <a style="cursor: pointer;" class="font-medium text-purple-600 hover:text-purple-500" (click)="onForgotPassword()">
                Forgot your password?
              </a>
            </div>
          </div>

          <div>
            <button type="submit" class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500">
              {{ isLoginMode ? 'Sign in' : 'Register' }}
            </button>
          </div>

          <div class="text-center">
            <button type="button" (click)="toggleMode()" class="text-sm text-purple-600 hover:text-purple-500">
              {{ isLoginMode ? 'Create new account' : 'Back to login' }}
            </button>
          </div>
        </form>

        <div *ngIf="errorMessage" class="mt-2 p-2 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <span class="block sm:inline">{{ errorMessage }}</span>
        </div>
      </div>

      <!-- Desktop Layout (Illustration + Login) -->
      <div class="hidden md:flex w-full h-screen">
        <!-- Illustration Column -->
        <div class="w-1/2 h-full flex flex-col items-center justify-center p-8 space-y-4 bg-purple-100">
          <div class="flex flex-col items-center justify-center space-y-4">
            <div class="w-32 h-32 rounded-full bg-purple-200 flex items-center justify-center">
              <svg class="w-20 h-20 text-purple-700" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-purple-900">Welcome Back!</h3>
            <p class="text-purple-700 text-center">Sign in to access your 3D modeling workspace</p>
          </div>
        </div>

        <!-- Form Column -->
        <div class="w-1/2 h-full flex flex-col items-center justify-center p-8 bg-white">
          <div class="max-w-md w-full space-y-8">
            <div>
              <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">
                {{ isLoginMode ? 'Log in to your account' : 'Create a new account' }}
              </h2>
            </div>
            <form class="mt-8 space-y-6" [formGroup]="authForm" (ngSubmit)="onSubmit()">
              <div class="rounded-md shadow-sm -space-y-px">
                <div>
                  <label for="email-address-desktop" class="sr-only">Email address</label>
                  <input id="email-address-desktop" name="email" type="email" formControlName="email" required class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm" placeholder="Email address">
                </div>
                <div>
                  <label for="password-desktop" class="sr-only">Password</label>
                  <input id="password-desktop" name="password" type="password" formControlName="password" required class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-purple-500 focus:border-purple-500 focus:z-10 sm:text-sm" placeholder="Password">
                </div>
              </div>

              <div class="flex items-center justify-between">
                <div class="flex items-center">
                  <input id="remember-me-desktop" formControlName="rememberMe" type="checkbox" class="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded">
                  <label for="remember-me-desktop" class="ml-2 block text-sm text-gray-900">
                    Remember me
                  </label>
                </div>

                <div class="text-sm">
                  <a style="cursor: pointer;" class="font-medium text-purple-600 hover:text-purple-500" (click)="onForgotPassword()">
                    Forgot your password?
                  </a>
                </div>
              </div>

              <div>
                <button type="submit" class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500">
                  {{ isLoginMode ? 'Sign in' : 'Register' }}
                </button>
              </div>

              <div class="text-center">
                <button type="button" (click)="toggleMode()" class="text-sm text-purple-600 hover:text-purple-500">
                  {{ isLoginMode ? 'Create new account' : 'Back to login' }}
                </button>
              </div>
            </form>

            <div *ngIf="errorMessage" class="mt-2 p-2 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
              <span class="block sm:inline">{{ errorMessage }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class LoginComponent implements OnInit {
  authForm: UntypedFormGroup;
  isLoginMode = true;
  errorMessage: string | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private _formBuilder: UntypedFormBuilder,
    private translate: TranslateService,
    public afAuth: AngularFireAuth,
    public afs: AngularFirestore,
    private authService: AuthService,
    private _snackBar: MatSnackBar,
    public dialog: MatDialog
  ) {
    console.log("LoginComponent Constructor Running (Restored)");
    this.authForm = this._formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      rememberMe: [false]
    });
  }

  ngOnInit() {
    console.log("LoginComponent ngOnInit Running (Restored)");
  }

  async logout() {
    if (confirm('Are you sure you want to log out?')) {
      try {
        await this.authService.signOut();
        console.log("User logged out successfully (Restored)");
        this._snackBar.open('Logged out successfully!', '', { duration: 3000 });
        this.router.navigate(['/login']);
      } catch (error) {
        console.error("Logout Error:", error);
        this._snackBar.open('Logout failed. Please try again.', '', { duration: 3000 });
      }
    }
  }

  get authState(): Observable<firebase.User | null> {
    return this.authService.authState;
  }

  async resetPassword(email: string) {
    try {
      await this.authService.sendPasswordResetEmail(email);
      this._snackBar.open('Password reset email sent! Check your inbox.', '', { duration: 5000 });
    } catch (error: any) {
      console.error("Error sending password reset email:", error);
      let message = 'Error sending password reset email. Please try again.';
      if (error.code === 'auth/user-not-found') {
        message = 'No user found with this email address.';
      } else if (error.code === 'auth/invalid-email') {
        message = 'Invalid email address format.';
      }
      this._snackBar.open(message, '', { duration: 6000 });
    }
  }

  async onSubmit() {
    this.errorMessage = null;
    if (!this.authForm.valid) {
      this.showFormErrors();
      return;
    }

    const { email, password } = this.authForm.value;

    if (this.isLoginMode) {
      await this.login(email, password);
    } else {
      await this.register(email, password);
    }
  }

  async login(email: string, password: string) {
    try {
      const rememberMe = this.authForm.get('rememberMe')?.value || false;
      const userCredential = await this.authService.signIn(email, password, rememberMe);
      if (userCredential.user) {
        console.log('Login successful, navigating to /dashboard');
        this.router.navigate(['/dashboard']);
      }
    } catch (error: any) {
      console.error("Login Error:", error);
      this.handleLoginError(error);
    }
  }

  async register(email: string, password: string) {
    try {
      this.errorMessage = null;
      const userCredential = await this.authService.signUp(email, password);
      this._snackBar.open('Registration successful! Please log in.', '', { duration: 5000 });
      this.isLoginMode = true;
      this.authForm.reset();

    } catch (error) {
      console.error("Registration Error:", error);
      this.handleRegistrationError(error);
    }
  }

  onForgotPassword() {
    const email = this.authForm.get('email')?.value;
    if (!email) {
      this._snackBar.open('Please enter your email address first.', '', { duration: 3000 });
      return;
    }
    this.resetPassword(email);
  }

  toggleMode() {
    this.isLoginMode = !this.isLoginMode;
    this.errorMessage = null;
    this.authForm.reset();
  }

  private showFormErrors() {
    let errorMessages = [];
    const emailControl = this.authForm.get('email');
    const passwordControl = this.authForm.get('password');

    if (emailControl?.hasError('required')) {
      errorMessages.push('Email address is required.');
    } else if (emailControl?.hasError('email')) {
      errorMessages.push('Please enter a valid email address.');
    }

    if (passwordControl?.hasError('required')) {
      errorMessages.push('Password is required.');
    } else if (passwordControl?.hasError('minlength')) {
      errorMessages.push('Password must be at least 6 characters long.');
    }

    this.errorMessage = errorMessages.join(' ');
  }

  private handleLoginError(error: any) {
    switch (error.code) {
      case 'auth/wrong-password':
        this.errorMessage = 'Incorrect password.';
        break;
      case 'auth/user-not-found':
        this.errorMessage = 'No user found with this email.';
        break;
      case 'auth/user-disabled':
        this.errorMessage = 'User account disabled.';
        break;
      case 'auth/too-many-requests':
        this.errorMessage = 'Too many login attempts.';
        break;
      case 'auth/invalid-email':
        this.errorMessage = 'Invalid email format.';
        break;
      default:
        this.errorMessage = 'Login error.';
    }
  }

  private handleRegistrationError(error: any) {
    switch (error.code) {
      case 'auth/email-already-in-use':
        this.errorMessage = 'Email already exists.';
        break;
      case 'auth/weak-password':
        this.errorMessage = 'Password is too weak.';
        break;
      case 'auth/invalid-email':
        this.errorMessage = 'Invalid email format.';
        break;
      default:
        this.errorMessage = `Registration failed: ${error.message}`;
    }
  }
}
